import {
  ReducerState,
  Submit
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { SubmissionData } from '@components/asset/modal/tabs/edit/submission';
import getCustomFieldChangesOLD, { CustomFieldChanges as CustomFieldChangesOLD } from './customFieldsChangeEngine';
import { getCustomFieldChanges, CustomFieldChanges } from './custom-fields-change-detection';
import getTagChanges from './tagChangeEngine';
import getAssetChanges from './assetChangeEngine';
import getAttachmentChanges from './attachmentChangeEngine';
import { CustomFieldValue } from '@/api/v4/assets/customFieldTypes';

// rolls up all the change map functionality
// so we can call this once for both submit button greying out and api call payloads
const detectChanges = (
  state: ReducerState,
  isSubmitting?: boolean,
): SubmissionData[] => {
  const assetChanges = getAssetChanges(state);
  let multiValueCustomFieldChanges: CustomFieldChanges;
  let customFieldChanges: Partial<CustomFieldChangesOLD> | false;
  if (BFG.hasFeature('rollout_multi_value_custom_fields')) {
    multiValueCustomFieldChanges = getCustomFieldChanges(state.editState, state.initialData);
  } else {
    customFieldChanges = getCustomFieldChangesOLD(state);
  }
  const tagChanges = getTagChanges(state);
  const attachmentChanges = getAttachmentChanges(state);

  const requiredSubmissions: SubmissionData[] = [];

  if (assetChanges) {
    requiredSubmissions.push({ action: Submit.UpdatedAsset, data: assetChanges });
  }

  if (customFieldChanges) {
    /** TODO: remove once multi value rolled out */
    const { createdCustomFieldMaps, deletedCustomFieldIds, updatedCustomFieldMaps } = customFieldChanges;
    if (createdCustomFieldMaps) {
      requiredSubmissions.push({ action: Submit.NewCustomFields, data: createdCustomFieldMaps });
    }

    if (updatedCustomFieldMaps) {
      updatedCustomFieldMaps.forEach((customFieldChange) => {
        requiredSubmissions.push({ action: Submit.UpdatedCustomField, data: customFieldChange });
      });
    }

    if (deletedCustomFieldIds) {
      deletedCustomFieldIds.forEach((id) => {
        requiredSubmissions.push({ action: Submit.DeletedCustomFieldValue, data: id });
      });
    }
  }

  if (multiValueCustomFieldChanges) {
    const {
      createdCustomFieldKeyValuePears,
      createdCustomFieldValues,
      deletedCustomFieldValueIds,
      updatedCustomFieldValues
    } = multiValueCustomFieldChanges;

    if (createdCustomFieldKeyValuePears) {
      createdCustomFieldKeyValuePears.forEach((pair) => {
        requiredSubmissions.push({ action: Submit.NewCustomFieldKeyValuePair, data: pair });
      });
    }

    if (createdCustomFieldValues) {
      requiredSubmissions.push({ action: Submit.NewCustomFieldValues, data: createdCustomFieldValues });
    }

    if (deletedCustomFieldValueIds) {
      deletedCustomFieldValueIds.forEach((deletedId) => {
        requiredSubmissions.push({ action: Submit.DeletedCustomFieldValue, data: deletedId });
      });
    }

    if (updatedCustomFieldValues) {
      Object.keys(updatedCustomFieldValues).forEach((updatedCFVId) => {
        const customFieldValue: CustomFieldValue = {
          key: updatedCFVId,
          value: updatedCustomFieldValues[updatedCFVId].value
        };
        requiredSubmissions.push({
          action: Submit.UpdatedCustomFieldValue,
          data: customFieldValue
        });
      });
    }
  }

  if (tagChanges) {
    requiredSubmissions.push({ action: Submit.Tags, data: tagChanges });
  }

  if (attachmentChanges) {
    const { deletedAttachmentKeys, updatedAttachments } = attachmentChanges;
    if (deletedAttachmentKeys) {
      deletedAttachmentKeys.forEach((key) => {
        requiredSubmissions.push({ action: Submit.DeletedAttachment, data: key });
      });
    }

    if (updatedAttachments) {
      updatedAttachments.forEach((updatedAttachment) => {
        requiredSubmissions.push({ action: Submit.UpdatedAttachment, data: updatedAttachment });
      });
    }
  }

  // used to trigger unsaved changes alert when closing asset modal
  if (isSubmitting) {
    BFG.editTabUnsavedChanges = false; // allows user to close asset modal while updates are submitting (i.e. no "unsaved changes" alert will trigger)
  } else {
    BFG.editTabUnsavedChanges = requiredSubmissions.length > 0;
  }

  return requiredSubmissions;
};

export default detectChanges;
